'use strict';

/**
 * @fileoverview `redactStack` — value-pattern redaction of a V8 stack trace (SECURITY-HARDENING-GEMS
 * Q-SG-24).
 *
 * A V8 stack is `<name>: <message>` followed by FRAMES, each on its own line starting `    at `. The
 * message is where data lives (a declined card, a duplicate key's email); the frames are file paths,
 * function names and line / column numbers — never user data. Scanning only the head keeps an error
 * record's cost proportional to its message rather than to its stack depth (measured: a 583-character
 * stack cost 14 µs to scan whole, on every error the server logs).
 *
 * @module @puffin-core/redaction/redactStack
 */

const { redactSensitiveText } = require('./redactSensitiveText');

/** Where the first V8 frame line begins. */
const FIRST_FRAME = '\n    at ';

/**
 * Redact sensitive data in a stack trace's message head, keeping its frames byte for byte.
 *
 * @param {*} stack - Any value; only a string is scanned, anything else is returned unchanged
 * @param {Object} options - The same options as `redactSensitiveText` (`fields` required, `placeholder`)
 * @returns {*} The redacted stack, or the input unchanged when it is not a string
 * @throws {TypeError} When `options.fields` is not an array of strings
 */
function redactStack(stack, options) {
  if (typeof stack !== 'string') {
    return redactSensitiveText(stack, options);
  }
  const framesAt = stack.indexOf(FIRST_FRAME);
  if (framesAt === -1) {
    return redactSensitiveText(stack, options);
  }
  return `${redactSensitiveText(stack.slice(0, framesAt), options)}${stack.slice(framesAt)}`;
}

module.exports = { redactStack };
